import { useModelsContents, useRimsContents } from "@/hooks/Contents";
import {
  useAsyncBrands,
  useAsyncModelsByBrand,
  useAsyncYearsByModelBrand
} from "@/hooks/Vehicles/Enums";
import {
  Button,
  Flex,
  Link,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  Select,
  SimpleGrid,
  Text,
  useToast
} from "@chakra-ui/react";
import { useRouter } from "next/router";
import { FormEvent, useEffect, useRef, useState } from "react";

export type ReturnedRimObject = {
  aro: string;
  slug: string;
};

export type ReturnedModelObject = {
  model: string;
  slug: string;
};

export type ReturnedYearObject = {
  year: string;
};

export function ModalPneu({ onClose }) {
  // ! Env
  const DefaultBrand = process.env.NEXT_PUBLIC_VEHICLE_SEARCH_BRAND;
  const DefaultModel = process.env.NEXT_PUBLIC_VEHICLE_SEARCH_MODEL;
  const DefaultYear = process.env.NEXT_PUBLIC_VEHICLE_SEARCH_YEAR;

  // ! Initial States
  const [Brands, setBrands] = useState<string[]>([]);
  const [SelectedBrand, setSelectedBrand] = useState<string>("");
  const [Models, setModels] = useState<string[]>([]);
  const [SelectedModel, setSelectedModel] = useState<string>("");
  const [Years, setYears] = useState<ReturnedYearObject[]>([]);
  const [SelectedYear, setSelectedYear] = useState<string>("");
  const [RimsList, setRimsList] = useState<ReturnedRimObject[]>([]);
  const [ModelsList, setModelsList] = useState<ReturnedModelObject[]>([]);
  const [ModelsListBF, setModelsListBF] = useState<ReturnedModelObject[]>([]);

  const [ModelsOrder, setModelsOrder] = useState<any>();

  // ! Runtime hooks
  const [RimData, RimError, RimLoading] = useRimsContents();
  const [ModelData, ModelError, ModelLoading] = useModelsContents("menu-modelos-linglong");
  const [ModelDataBF, ModelErrorBF, ModelLoadingBF] = useModelsContents("menu-modelos-linglong");

  // ! Controller & Rullers
  const Router = useRouter();
  const BrandRef = useRef<HTMLSelectElement>(null);
  const Toast = useToast({
    position: "bottom",
    duration: 5000,
    isClosable: true
  });

  function orderModels() {
    const Ordened = ModelsList?.sort((a, b) => {
      if (a.model > b.model) {
        return 1;
      } else if (b.model > a.model) {
        return -1;
      } else {
        return 0;
      }
    });
    setModelsOrder(Ordened);
  }

  // ! Methods
  async function requestBrands(DefaultBrand: string = null) {
    const [BData, BError] = await useAsyncBrands();
    if (BData && BError === null) {
      setBrands(BData);
      return true;
    } else {
      return false;
    }
  }
  async function requestModelsByBrand(BrandDefault: string = null) {
    const [MData, MError] = await useAsyncModelsByBrand(BrandDefault ?? SelectedBrand);
    if (MData && MError === null) {
      setModels(MData);
      setSelectedModel(MData?.[0]);
      const Brand = BrandRef?.current?.selectedOptions?.[0]?.value;
      requestYearsByBrandModel(Brand, MData?.[0]);
      return true;
    } else {
      return false;
    }
  }
  async function requestYearsByBrandModel(
    BrandDefault: string = null,
    ModelDefault: string = null
  ) {
    const [YData, YError] = await useAsyncYearsByModelBrand(
      BrandDefault ?? SelectedBrand,
      ModelDefault ?? SelectedModel
    );
    if (YData && YError === null) {
      setYears(YData);
      setSelectedYear(null);
      return true;
    } else {
      return false;
    }
  }
  async function SubmitVehicleSearch(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    Toast.closeAll();
    switch (true) {
      case !Boolean(SelectedBrand):
        Toast({
          title: "Selecione uma marca para realizar a pesquisa.",
          status: "info"
        });
        return false;
      case !Boolean(SelectedModel):
        Toast({
          title: "Selecione um modelo para realizar a pesquisa.",
          status: "info"
        });
        return false;
      default:
        Router.push({
          pathname: "/pesquisa/veiculo",
          query: Boolean(SelectedYear)
            ? {
                brand: `${SelectedBrand}`,
                model: `${SelectedModel}`,
                year: `${SelectedYear ?? ""}`
              }
            : {
                brand: `${SelectedBrand}`,
                model: `${SelectedModel}`
              }
        });
    }
  }

  // ! Effects
  useEffect(() => {
    orderModels();
  }, [ModelsList]);
  useEffect(() => setRimsList(RimData?.[0]?.contentsArray), [RimData]);
  useEffect(() => setModelsList(ModelData?.[0]?.contentsArray), [ModelData]);
  useEffect(() => setModelsListBF(ModelDataBF?.[0]?.contentsArray), [ModelDataBF]);
  useEffect(() => {
    requestBrands(DefaultBrand).then(async (bool) => {
      if (bool) {
        setSelectedBrand(DefaultBrand);
        await requestModelsByBrand(DefaultBrand).then(async (bool) => {
          if (bool) {
            setSelectedModel(DefaultModel);
            await requestYearsByBrandModel(DefaultBrand, DefaultModel).then((bool) => {
              setSelectedYear(DefaultYear);
            });
          }
        });
      }
    });
  }, []);

  // ! Main return
  return (
    <PopoverContent
      boxShadow={"0 0 10px #666"}
      bgColor={"white"}
      zIndex={999}
      w={"800px"}
      // ml={"300px"}
      borderRadius={"20px"}
      mt={"10px"}
      h={"auto"}
      py={"20px"}
      textStyle={"Regular"}
    >
      <PopoverArrow />
      <Flex mr="15px" alignSelf="flex-end"></Flex>
      <PopoverCloseButton marginRight={"5px"} mt={"5px"} />
      <PopoverBody>
        <Flex
          h={"full"}
          w={"full"}
          sx={{
            ".menu__item": {
              ml: "-5px",
              px: "5px",
              py: "3px",
              _hover: {
                color: "white",
                bgColor: "#1068D9",
                textDecoration: "none"
              }
            },
            ".sm_link": {
              w: "120px"
            },
            ".md_link": {
              w: "250px"
            }
          }}
        >
          {RimsList?.length > 0 && (
            <Flex
              flex={0.4}
              color={"#1E1E1E"}
              _hover={{ fontStyle: "none" }}
              direction={"column"}
              borderRightColor={"gray.300"}
              borderRightStyle={"solid"}
              borderRightWidth={"1px"}
              pl={"20px"}
            >
              <Text textStyle={"Bold"} color={"#1E1E1E"} fontWeight={"bold"}fontSize={"text1"} mb={"5px"}>
                AROS
              </Text>
              <Flex direction={"column"} w={"80px"}>
                {RimsList.map((rim, index) => {
                  return (
                    <Link
                      textColor={"#58585A"}
                      key={index}
                      className={"menu__item"}
                      href={`/pesquisa/PNEU?Rim=${rim.aro.replaceAll("ARO", "")}`}
                    >
                      {rim.aro}
                    </Link>
                  );
                })}
              </Flex>
            </Flex>
          )}
          <Flex
            flex={4}
            direction={"column"}
            pl={"20px"}
            borderRightColor={"gray.300"}
            borderRightStyle={"solid"}
            borderRightWidth={"1px"}
          >
            {ModelsList?.length > 0 && (
              <>
                <Text textStyle={"Bold"} color={"#1E1E1E"} fontWeight={"bold"} fontSize={"text1"} mb={"5px"}>
                  MODELOS
                </Text>
                <Flex maxH={"350px"} direction={"column"} flexWrap={"wrap"}>
                  {ModelsOrder?.map((tires, index) => {
                    return (
                      <Link
                        textStyle="Regular"
                        color="#3B3B3B"
                        key={index}
                        className="menu__item"
                        href={`${tires.slug.split(" ").join("-")}`}
                      >
                        {tires.model}
                      </Link>
                    );
                  })}
                </Flex>
              </>
            )}
          </Flex>
          <Flex flex="1.5" direction="column" pl="20px">
            <Text
              alignItems="left"
              textStyle={"Bold"}
              color={"#1E1E1E"}
              fontWeight={"bold"}
              fontSize={"text1"}
              mb="20px"
            >
              VEÍCULOS
            </Text>
            <Flex as={"form"} onSubmit={SubmitVehicleSearch} direction={"column"} w={"90%"}>
              <Select
                borderRadius={"50px"}
                _focus={{ borderColor: "#58585A", boxShadow: "none" }}
                mb={"20px"}
                placeholder="MARCA"
                ref={BrandRef}
                onChange={(e) => {
                  setSelectedBrand(e.currentTarget.value);
                  setSelectedYear(null);
                  requestModelsByBrand(e.currentTarget.value);
                }}
              >
                {Brands.length > 0 &&
                  Brands.map((brand, index) => {
                    return (
                      <option key={index} selected={brand === SelectedBrand} value={brand}>
                        {brand}
                      </option>
                    );
                  })}
              </Select>
              <Select
                _focus={{ borderColor: "#58585A", boxShadow: "none" }}
                borderRadius={"50px"}
                mb={"20px"}
                placeholder="MODELO"
                onChange={(e) => {
                  setSelectedModel(e.currentTarget.value);
                  requestYearsByBrandModel(SelectedBrand, e.currentTarget.value);
                }}
              >
                {Models.length > 0 &&
                  Models.map((model, index) => {
                    return (
                      <option key={index} selected={model === SelectedModel} value={model}>
                        {model}
                      </option>
                    );
                  })}
              </Select>
              <Select
                _focus={{ borderColor: "#58585A", boxShadow: "none" }}
                borderRadius={"50px"}
                mb={"20px"}
                placeholder="ANO"
                onChange={(e) => setSelectedYear(e.currentTarget.value)}
              >
                {Years.length > 0 &&
                  Years.map((comp, index) => {
                    return (
                      <option key={index} selected={comp.year === SelectedYear} value={comp.year}>
                        {comp.year}
                      </option>
                    );
                  })}
              </Select>
              <Button
                borderRadius="50px"
                textStyle="Bold"
                type="submit"
                color="white"
                bgColor="#00A550"
                onClick={() => onClose()}
                _hover={{ opacity: "80%" }}
              >
                PESQUISAR
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </PopoverBody>
    </PopoverContent>
  );
}
